import { useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useAuth } from '@clerk/react';
import log from 'loglevel';

import { clearAllUserState } from '../utils/clearAllUserState';

// Set while a session is active, so a signed-out page load can tell a session that ended while the
// tab was closed (clear) from a visitor who never signed in (leave their storage alone; the sign-up
// page keeps first-touch attribution there).
export const HAD_SESSION_STORAGE_KEY = 'tt_had_session';

const readHadSession = (): boolean => {
  try {
    return localStorage.getItem(HAD_SESSION_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const markHadSession = (): void => {
  try {
    localStorage.setItem(HAD_SESSION_STORAGE_KEY, '1');
  } catch {
    // Storage unavailable (private mode); the in-tab transition below still clears.
  }
};

/**
 * Clears cached user data whenever a session ends, not only on an explicit sign-out (CASA 6.6.1):
 * a session that expired or was revoked from another device, noticed in this tab or on the next
 * load. The explicit sign-out path clears too; doing it twice is harmless.
 */
export const useClearStateOnSessionEnd = (): void => {
  const { isLoaded, isSignedIn } = useAuth();
  const apolloClient = useApolloClient();
  const wasSignedIn = useRef(false);

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn) {
      wasSignedIn.current = true;
      markHadSession();
      return;
    }

    if (!wasSignedIn.current && !readHadSession()) return;

    wasSignedIn.current = false;
    // clearAllStorages inside clearAllUserState removes the marker with the rest.
    clearAllUserState(apolloClient).catch((error) =>
      log.error('Failed to clear user state after the session ended', error),
    );
  }, [isLoaded, isSignedIn, apolloClient]);
};
